import { useState, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  RowSelectionState,
  Row,
} from '@tanstack/react-table';
import { Pagination } from '../Pagination/Index';
import { IconSort, IconSortArrow } from '../Icon/Index';
import { TableType } from './types';

const Table = <T extends object>(props: TableType<T>) => {
  const {
    columns,
    data,
    totalRecords,
    isLoading,
    sorting,
    setSorting,
    manualSorting = true,
    serverSidePagination = true,
    pagination,
    enablePagination = true,
    onPageChange = null,
    onRowClick,
    className = '',
    ...rest
  } = props;

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const table = useReactTable({
    data: data,
    columns: columns,
    state: {
      pagination,
      sorting,
      rowSelection,
    },
    manualSorting: manualSorting,
    enableMultiRowSelection: false,
    onRowSelectionChange: setRowSelection,
    manualPagination: false,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: serverSidePagination
      ? undefined
      : getPaginationRowModel(),
    pageCount:
      totalRecords < pagination.pageSize
        ? 1
        : Math.ceil(totalRecords / pagination.pageSize),
  });

  const isNoDataFound = !data || data.length === 0;

  const [totalPages, setTotalPages] = useState<number>(0);

  useEffect(() => {
    setTotalPages(Math.ceil(totalRecords / pagination.pageSize));
  }, [totalRecords, pagination]);

  const getPageRange = () => {
    const start = pagination.pageSize * pagination.pageIndex + 1;
    const end = Math.min(start + pagination.pageSize - 1, totalRecords);
    return `${start}-${end}`;
  };

  useEffect(() => {
    const clickedRow: Row<T>[] = table.getSelectedRowModel().rows;

    if (
      Array.isArray(clickedRow) &&
      clickedRow[0] !== undefined &&
      clickedRow[0].original !== undefined
    ) {
      if (onRowClick) onRowClick(clickedRow[0].original);
    }
  }, [rowSelection]);

  return (
    <>
      <div
        className={`theme-table-container theme-table-max-height ${className}`}
        {...rest}
      >
        {isLoading && (
          <div className='theme-table-loading'>
            <p>Loading table data..</p>
          </div>
        )}
        <table className={`theme-table`}>
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      style={{
                        width: header.getSize(),
                      }}
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? 'theme-table-header-wrapper'
                              : '',
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {{
                            asc: (
                              <IconSortArrow
                                fill='#395278'
                                className='theme-table-sort-icon'
                              />
                            ),
                            desc: (
                              <IconSortArrow
                                fill='#395278'
                                className='theme-table-sort-icon theme-table-sort-icon-desc'
                              />
                            ),
                          }[header.column.getIsSorted() as string] ??
                            (header.column.columnDef.enableSorting ===
                              undefined ||
                            header.column.columnDef.enableSorting === true ? (
                              <IconSort
                                fill='#395278'
                                className='theme-table-sort-icon'
                              />
                            ) : null)}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {isNoDataFound ? (
              <tr>
                <td colSpan={100}>No data found</td>
              </tr>
            ) : (
              <>
                {table.getRowModel().rows.map(row => (
                  <tr key={row.id} onClick={row.getToggleSelectedHandler()}>
                    {row.getVisibleCells().map(cell => {
                      return (
                        <td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>
      {enablePagination && totalPages > 0 && (
        <div className='theme-table-pagination'>
          <span className='theme-table-pagination-text'>
            Showing item {getPageRange()} out of {totalRecords} items
          </span>
          <Pagination
            className='theme-table-pagination-paging'
            currentPage={pagination.pageIndex + 1}
            totalPages={totalPages}
            onPageChange={value => onPageChange && onPageChange(value - 1)}
          />
        </div>
      )}
    </>
  );
};

export default Table;
